// src/components/HomeworkDetailGuideOverlay.js
import React, { useState, useLayoutEffect, useRef } from 'react';
import useGuideScrollExtension from '../utils/useGuideScrollExtension';
import ReactDOM from 'react-dom';

const BREAKPOINT = 900;
const TIP = 15;
const SPOT_PAD = 12;

const HomeworkDetailGuideOverlay = ({
    onClose,
    mode          = 'manual',
    autoStep      = null,   // 'pre' | 'post'
    hasTutorials  = false,
    onHeightChange = null,
}) => {
    const [isSmallScreen, setIsSmallScreen] = useState(() => window.innerWidth < BREAKPOINT);
    const [positions, setPositions] = useState(null);
    const box1Ref = useRef(null); // [post] Progress Stats
    const box2Ref = useRef(null); // [post] Preview
    const box3Ref = useRef(null); // [post] Link
    const box4Ref = useRef(null); // [pre]  Tutorial Content
    const box5Ref = useRef(null); // [pre]  Generate Tutorial
    const box6Ref = useRef(null); // [post] Progress

    useLayoutEffect(() => {
        const calculate = () => {
            const vw = window.innerWidth;
            const vh = window.innerHeight;
            const scrollY = window.scrollY;

            if (vw < BREAKPOINT) {
                setIsSmallScreen(true);
                setPositions(null);
                return;
            }
            setIsSmallScreen(false);

            const active = mode === 'auto'
                ? new Set([autoStep])
                : (hasTutorials ? new Set(['post']) : new Set(['pre']));

            const compositionEl   = document.querySelector('[data-guide="hd-composition"]');
            const previewEl       = document.querySelector('[data-guide="hd-preview"]');
            const linkEl          = document.querySelector('[data-guide="hd-link"]');
            const generateAllEl   = document.querySelector('[data-guide="hd-generate-all"]');
            const progressStatsEl = document.querySelector('[data-guide="hd-progress-stats"]');
            const progressEl      = document.querySelector('[data-guide="hd-progress"]');

            if (!compositionEl) return;

            const pad = 10;
            const newPos = {};

            // ── Post-state ────────────────────────────────────────────────────────────────
            if (active.has('post') && box1Ref.current && box2Ref.current && box3Ref.current && box6Ref.current) {
                const b1 = box1Ref.current.getBoundingClientRect();
                const b2 = box2Ref.current.getBoundingClientRect();
                const b3 = box3Ref.current.getBoundingClientRect();
                const b6 = box6Ref.current.getBoundingClientRect();
                const spots = [];

                // Box 1: Progress Stats — arrowDown, bubble above button
                if (progressStatsEl) {
                    const pe = progressStatsEl.getBoundingClientRect();
                    const tipX = pe.left + pe.width / 2;
                    const tipY = pe.top + scrollY;
                    const boxLeft   = Math.max(pad, Math.min(tipX - b1.width / 2, vw - b1.width - pad));
                    const arrowLeft = Math.min(Math.max(TIP, tipX - boxLeft), b1.width - TIP);
                    newPos.box1 = { top: tipY - TIP - b1.height, left: boxLeft, arrowLeft };
                    spots.push({ x: pe.left, y: pe.top, w: pe.width, h: pe.height });
                }

                // Box 2: Preview — arrowDown, bubble above button
                if (previewEl) {
                    const ae = previewEl.getBoundingClientRect();
                    const tipX = ae.left + ae.width / 2;
                    const tipY = ae.top + scrollY;
                    const boxLeft   = Math.max(pad, Math.min(tipX - b2.width / 2, vw - b2.width - pad));
                    const arrowLeft = Math.min(Math.max(TIP, tipX - boxLeft), b2.width - TIP);
                    newPos.box2 = { top: tipY - TIP - b2.height, left: boxLeft, arrowLeft };
                    spots.push({ x: ae.left, y: ae.top, w: ae.width, h: ae.height });
                }

                // Box 3: Link — arrowUp, bubble below button
                let leRect = null;
                if (linkEl) {
                    leRect = linkEl.getBoundingClientRect();
                    const tipX = leRect.left + leRect.width / 2;
                    const boxLeft   = Math.max(pad, Math.min(tipX - b3.width / 2, vw - b3.width - pad));
                    const arrowLeft = Math.min(Math.max(TIP, tipX - boxLeft), b3.width - TIP);
                    newPos.box3 = { top: leRect.bottom + scrollY + TIP, left: boxLeft, arrowLeft };
                }

                // Box 6: Progress — arrowLeft, bubble to the right of button
                let geRect = null;
                if (progressEl) {
                    geRect = progressEl.getBoundingClientRect();
                    const boxLeft = Math.min(geRect.right + TIP + 5, vw - b6.width - pad);
                    const boxTop  = Math.max(pad, geRect.top + scrollY + geRect.height / 2 - b6.height / 2);
                    newPos.box6 = { top: boxTop, left: boxLeft };
                }

                // Merge Link + Progress into one spotlight if both exist, otherwise push individually
                if (leRect && geRect) {
                    const mx = Math.min(leRect.left, geRect.left);
                    const my = Math.min(leRect.top, geRect.top);
                    spots.push({ x: mx, y: my, w: Math.max(leRect.right, geRect.right) - mx, h: Math.max(leRect.bottom, geRect.bottom) - my });
                } else if (leRect) {
                    spots.push({ x: leRect.left, y: leRect.top, w: leRect.width, h: leRect.height });
                } else if (geRect) {
                    spots.push({ x: geRect.left, y: geRect.top, w: geRect.width, h: geRect.height });
                }

                if (spots.length) newPos.spotlights = spots;
            }

            // ── Pre-state ─────────────────────────────────────────────────────────────────
            if (active.has('pre') && box4Ref.current && box5Ref.current) {
                const b4 = box4Ref.current.getBoundingClientRect();
                const b5 = box5Ref.current.getBoundingClientRect();
                const spots = [];

                const summaryEl = compositionEl.querySelector('summary') ?? compositionEl;
                const se = summaryEl.getBoundingClientRect();
                const tipX4 = se.left + 120;
                const tipY4 = se.top + scrollY;
                const boxLeft4   = Math.max(pad, Math.min(tipX4 - b4.width / 2, vw - b4.width - pad));
                const arrowLeft4 = Math.min(Math.max(TIP, tipX4 - boxLeft4), b4.width - TIP);
                newPos.box4 = { top: tipY4 - TIP - b4.height, left: boxLeft4, arrowLeft: arrowLeft4 };
                spots.push({ x: se.left, y: se.top, w: se.width, h: se.height });

                if (generateAllEl) {
                    const ge = generateAllEl.getBoundingClientRect();
                    const tipX5 = ge.left + ge.width / 2;
                    const tipY5 = ge.top + scrollY;
                    const boxLeft5   = Math.max(pad, Math.min(tipX5 - b5.width / 2, vw - b5.width - pad));
                    const arrowLeft5 = Math.min(Math.max(TIP, tipX5 - boxLeft5), b5.width - TIP);
                    newPos.box5 = { top: tipY5 - TIP - b5.height, left: boxLeft5, arrowLeft: arrowLeft5 };
                    spots.push({ x: ge.left, y: ge.top, w: ge.width, h: ge.height });
                }

                if (spots.length) newPos.spotlights = spots;
            }

            // ── Overlay height: extend page if any bubble goes below normal content ────────
            const docHeight = document.documentElement.scrollHeight;
            const allBoxRefs = [box1Ref, box2Ref, box3Ref, box4Ref, box5Ref, box6Ref];
            const allBoxKeys = ['box1', 'box2', 'box3', 'box4', 'box5', 'box6'];
            let maxBottom = docHeight;
            allBoxRefs.forEach((ref, i) => {
                const pos = newPos[allBoxKeys[i]];
                if (ref.current && pos?.top !== undefined) {
                    const h = ref.current.getBoundingClientRect().height || 180;
                    maxBottom = Math.max(maxBottom, pos.top + h + 60);
                }
            });
            newPos.overlayHeight = maxBottom;

            if (onHeightChange) onHeightChange(maxBottom);
            setPositions(newPos);
        };

        calculate();
        window.addEventListener('resize', calculate);
        window.addEventListener('scroll', calculate, { passive: true });
        return () => {
            window.removeEventListener('resize', calculate);
            window.removeEventListener('scroll', calculate);
        };
    }, [mode, autoStep, hasTutorials, onHeightChange]);

    useGuideScrollExtension(!isSmallScreen);

    if (isSmallScreen) return null;

    const overlayHeight = positions?.overlayHeight ?? document.documentElement.scrollHeight;
    const spots = positions?.spotlights ?? [];

    if (mode === 'auto' && (!positions || spots.length === 0)) return null;

    // ── Styles ───────────────────────────────────────────────────────────────────

    const overlayStyle = {
        position: 'absolute', top: 0, left: 0, width: '100%',
        height: `${overlayHeight}px`,
        zIndex: 9999,
        cursor: 'pointer',
        ...(mode === 'manual' ? { backgroundColor: 'rgba(45, 64, 66, 0.6)' } : {}),
        fontFamily: '"Comic Sans MS", "Chalkboard SE", "Marker Felt", sans-serif',
    };

    const boxBase = {
        position: 'absolute',
        backgroundColor: 'white',
        border: '3px solid #2D4042',
        borderRadius: '255px 15px 225px 15px / 15px 225px 15px 255px',
        padding: '20px',
        boxShadow: '8px 8px 0px rgba(0,0,0,0.3)',
        pointerEvents: mode === 'auto' ? 'auto' : 'none',
    };

    const markerStyle = {
        position: 'absolute', top: '-15px', left: '-15px',
        width: '35px', height: '35px', backgroundColor: '#75B9BE', color: 'white',
        borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 'bold', fontSize: '1.2rem', border: '2px solid #2D4042',
        boxShadow: '3px 3px 0px rgba(0,0,0,0.2)',
    };

    const titleStyle = {
        margin: '0 0 5px 0', color: '#2D4042', fontSize: '1.1rem',
        fontWeight: '900', textTransform: 'uppercase', letterSpacing: '1px',
    };

    const textStyle = { margin: 0, color: '#555', fontSize: '0.95rem', lineHeight: '1.4' };

    const getBoxStyle = (posData, maxWidth) => {
        if (!posData) return { ...boxBase, maxWidth, visibility: 'hidden', top: -9999, left: -9999 };
        return { ...boxBase, maxWidth, visibility: 'visible', top: posData.top, left: posData.left };
    };

    const makeArrowDown = (arrowLeft) => ({
        position: 'absolute', bottom: '-15px', left: arrowLeft ?? '50%',
        width: 0, height: 0,
        borderLeft: '15px solid transparent', borderRight: '15px solid transparent',
        borderTop: '15px solid white', filter: 'drop-shadow(0px 3px 0px #2D4042)',
    });

    const makeArrowUp = (arrowLeft) => ({
        position: 'absolute', top: '-15px', left: arrowLeft ?? '50%',
        width: 0, height: 0,
        borderLeft: '15px solid transparent', borderRight: '15px solid transparent',
        borderBottom: '15px solid white', filter: 'drop-shadow(0px -3px 0px #2D4042)',
    });

    const arrowLeftStyle = {
        position: 'absolute', top: '50%', left: '-15px',
        width: 0, height: 0,
        borderTop: '10px solid transparent', borderBottom: '10px solid transparent',
        borderRight: '15px solid white', transform: 'translateY(-50%)',
        filter: 'drop-shadow(-3px 0px 0px #2D4042)',
    };

    const closeBtnStyle = {
        position: 'absolute', top: '6px', right: '8px',
        background: 'none', border: 'none', cursor: 'pointer',
        fontSize: '1.1rem', color: '#2D4042', fontWeight: 'bold', lineHeight: 1, padding: '2px 4px',
    };

    return ReactDOM.createPortal(
        <div style={overlayStyle} onClick={onClose}>

            {/* SVG spotlight — fixed to viewport, coordinates are always viewport-relative */}
            {mode === 'auto' && (
                <svg style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
                    <style>{`
                        @keyframes guide-pulse {
                            0%, 100% { opacity: 1; }
                            50% { opacity: 0.4; }
                        }
                        .guide-spotlight-ring { animation: guide-pulse 1.8s ease-in-out infinite; }
                    `}</style>
                    <defs>
                        <mask id="guide-spotlight-mask">
                            <rect width="100%" height="100%" fill="white" />
                            {spots.map((s, i) => (
                                <rect key={i}
                                    x={s.x - SPOT_PAD} y={s.y - SPOT_PAD}
                                    width={s.w + SPOT_PAD * 2} height={s.h + SPOT_PAD * 2}
                                    rx="8" fill="black"
                                />
                            ))}
                        </mask>
                    </defs>
                    <rect width="100%" height="100%" fill="rgba(45,64,66,0.78)" mask="url(#guide-spotlight-mask)" />
                    {spots.map((s, i) => (
                        <rect key={i}
                            className="guide-spotlight-ring"
                            x={s.x - SPOT_PAD} y={s.y - SPOT_PAD}
                            width={s.w + SPOT_PAD * 2} height={s.h + SPOT_PAD * 2}
                            rx="8" fill="none" stroke="#75B9BE" strokeWidth="3"
                        />
                    ))}
                </svg>
            )}

            {/* Post-state Box 1: Progress Stats 📊 */}
            <div data-guide-popup="true" ref={box1Ref} style={getBoxStyle(positions?.box1, '270px')}>
                {mode === 'auto' && <button style={closeBtnStyle} onClick={(e) => { e.stopPropagation(); onClose(); }}>×</button>}
                <div style={markerStyle}>📊</div>
                <p style={titleStyle}>1. Progress Stats</p>
                <p style={textStyle}>
                    Toggle class-wide stats to see attempts, scores, and time on task for every student at a glance.
                </p>
                <div style={makeArrowDown(positions?.box1?.arrowLeft)} />
            </div>

            {/* Post-state Box 2: Preview 👁️ */}
            <div data-guide-popup="true" ref={box2Ref} style={getBoxStyle(positions?.box2, '260px')}>
                {mode === 'auto' && <button style={closeBtnStyle} onClick={(e) => { e.stopPropagation(); onClose(); }}>×</button>}
                <div style={markerStyle}>👁️</div>
                <p style={titleStyle}>2. Preview</p>
                <p style={textStyle}>
                    Click <strong>Preview</strong> to view the full personalised tutorial before sending it to the student.
                </p>
                <div style={makeArrowDown(positions?.box2?.arrowLeft)} />
            </div>

            {/* Post-state Box 3: Link 🔗 */}
            <div data-guide-popup="true" ref={box3Ref} style={getBoxStyle(positions?.box3, '260px')}>
                {mode === 'auto' && <button style={closeBtnStyle} onClick={(e) => { e.stopPropagation(); onClose(); }}>×</button>}
                <div style={markerStyle}>🔗</div>
                <p style={titleStyle}>3. Link</p>
                <p style={textStyle}>
                    Click <strong>Link</strong> to generate a unique student link. Share it directly or email it — the student can access their personalised tutorial instantly!
                </p>
                <div style={makeArrowUp(positions?.box3?.arrowLeft)} />
            </div>

            {/* Pre-state Box 4: Tutorial Content 📝 */}
            <div data-guide-popup="true" ref={box4Ref} style={getBoxStyle(positions?.box4, '280px')}>
                {mode === 'auto' && <button style={closeBtnStyle} onClick={(e) => { e.stopPropagation(); onClose(); }}>×</button>}
                <div style={markerStyle}>📝</div>
                <p style={titleStyle}>1. Tutorial Content</p>
                <p style={textStyle}>
                    Choose which learning objectives and key terms to include before generating. ClassLabs tailors each tutorial to exactly what you select!
                </p>
                <div style={makeArrowDown(positions?.box4?.arrowLeft)} />
            </div>

            {/* Pre-state Box 5: Generate Tutorial ✨ */}
            <div data-guide-popup="true" ref={box5Ref} style={getBoxStyle(positions?.box5, '260px')}>
                {mode === 'auto' && <button style={closeBtnStyle} onClick={(e) => { e.stopPropagation(); onClose(); }}>×</button>}
                <div style={markerStyle}>✨</div>
                <p style={titleStyle}>2. Generate Tutorial</p>
                <p style={textStyle}>
                    Once you're happy with your composition selections, click <strong>Generate All</strong> to instantly build a personalised tutorial for every student!
                </p>
                <div style={makeArrowDown(positions?.box5?.arrowLeft)} />
            </div>

            {/* Post-state Box 6: Progress 📈 */}
            <div data-guide-popup="true" ref={box6Ref} style={getBoxStyle(positions?.box6, '270px')}>
                {mode === 'auto' && <button style={closeBtnStyle} onClick={(e) => { e.stopPropagation(); onClose(); }}>×</button>}
                <div style={markerStyle}>📈</div>
                <p style={titleStyle}>4. Progress</p>
                <p style={textStyle}>
                    Once a student has completed their tutorial, click <strong>Progress</strong> to view a full breakdown of their attempt, including question-by-question performance.
                </p>
                <div style={arrowLeftStyle} />
            </div>

            <p style={{ position: 'fixed', bottom: '20px', width: '100%', textAlign: 'center', color: 'white', fontWeight: 'bold', fontSize: '1.2rem', textShadow: '2px 2px 4px rgba(0,0,0,0.5)', pointerEvents: 'none' }}>
                Click anywhere to close
            </p>
        </div>,
        document.body
    );
};

export default HomeworkDetailGuideOverlay;
